import { useMutation } from "@tanstack/react-query";
import { useAtomValue, useSetAtom } from "jotai";
import TenantAPI from "../../../api/api/TenantAPI";
import useCloudPermissions from "../../../hooks/useCloudPermissions";
import { callFunctionIfExists } from "../../../utils/GeneralUtils";
import { getErrorMessage } from "../../../utils/GeneralUtils";
import { schemaAtom, setSchemaAtom } from "../../../atoms/GlobalAtoms";
import { subitemDrawerAtom } from "../../../atoms/PageAtoms";

interface UpdateSubitemField {
  moduleAPIName: string;
  fieldAPIName: string;
  payload: object;
  onSuccess?: () => void;
}

const useUpdateSubitemField = () => {
  const { cPermissions } = useCloudPermissions();
  const setSubitemDrawer = useSetAtom(subitemDrawerAtom);
  const schema = useAtomValue(schemaAtom);
  const setSchema = useSetAtom(setSchemaAtom);

  const updateSubitemField = useMutation({
    mutationFn: ({
      moduleAPIName,
      fieldAPIName,
      payload,
    }: UpdateSubitemField) => {
      if (!cPermissions.cloud_modify_field) {
        Promise.reject("You do not have permission to modify this field.");
      }

      return TenantAPI.updateSubitemField(moduleAPIName, fieldAPIName, payload);
    },
    onMutate: ({ moduleAPIName, fieldAPIName, payload }) => {
      if (!cPermissions.cloud_modify_template || !schema) return;

      const previousSchema = schema;

      schema.CRM.Modules = schema.CRM.Modules.map((module) => {
        if (module.APIName === moduleAPIName) {
          module.Fields = module.Fields.map((field) => {
            if (field.APIName === fieldAPIName) {
              field = { ...field, ...payload };
              setSubitemDrawer((prev) => ({ ...prev, field: field }));
              return field;
            }

            return field;
          });
        }
        return module;
      });

      setSchema(schema);
      return { previousSchema };
    },
    onSuccess: (_, { onSuccess }) => {
      callFunctionIfExists(onSuccess);
    },
    onError: (err, _, context) => {
      console.log(getErrorMessage(err));
    },
  });
  return {
    updateSubitemField: updateSubitemField.mutate,
    updatingSubitem: updateSubitemField.isPending,
    updateSubitemError: updateSubitemField.error
      ? getErrorMessage(updateSubitemField.error)
      : null,
  };
};

export default useUpdateSubitemField;
